import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { SWRConfiguration } from 'swr';
import { option } from '../../components/CustomFieldComponent';
import {
    AutoCompleteEndpointMap,
    AutoCompleteKeyMap,
    autoCompleteItemNameListCreatedBySystem
} from '../../components/GrispiInput/components/GrispiAutoCompleteInput';
import { DEDUP_INTERVAL_AUTOCOMPLETE_SUGGESTION } from '../../constants/swr-config';
import { Group, IForm, Organization } from '../../model/types';
import { endUserPublicFetcher, fetcherWithToken } from '../../utils/fetchers';
import { nonNullishValue } from '../../utils/helpers';
import useSWRCacheKeys, { usePublicApiSWRCacheKeys } from '../useSWRCacheKeys';
import { AutocompleteResponseType } from './useAutocompleteSearch';

export default function useAutocompleteSuggestions<T extends Group | Organization | IForm>(
    autocompleteKey: AutoCompleteKeyMap,
    needSuggestions: boolean,
    isPublic: boolean = false,
    config: SWRConfiguration = {}
) {
    const { t } = useTranslation('forms');

    const keys = useSWRCacheKeys(`/field-values/${AutoCompleteEndpointMap[autocompleteKey]}/options`);
    const publicKeys = usePublicApiSWRCacheKeys(`/field-values/${AutoCompleteEndpointMap[autocompleteKey]}/options`);

    const [isSuggestionRequested, setIsSuggestionRequested] = useState(false);

    const isKeyExists = nonNullishValue(keys) || nonNullishValue(publicKeys);

    const isAbleToMakeRequest = isKeyExists && needSuggestions && isSuggestionRequested;
    const { data, error, isLoading, mutate } = useSWR<AutocompleteResponseType<T>>(
        isAbleToMakeRequest ? (isPublic ? publicKeys : keys) : null,
        isPublic ? endUserPublicFetcher : fetcherWithToken,
        { revalidateIfStale: true, dedupingInterval: DEDUP_INTERVAL_AUTOCOMPLETE_SUGGESTION, ...config }
    );
    const searchResultAsOptions = useMemo(
        () =>
            data?.result?.map((item) => ({
                label: autoCompleteItemNameListCreatedBySystem.includes(item.name)
                    ? t(`form.autoCompleteItems.${item.name}` as any)
                    : item.name,
                value: item.id
            })),
        [data?.result, t]
    );

    return {
        data: searchResultAsOptions as option[],
        error: error?.message,
        isLoading,
        mutate,
        setIsSuggestionRequested
    };
}
